import { c as _c } from "react/compiler-runtime";
// Модалка «Заявка на возврат» + «Ваша заявка на возврат принята!» (макет
// «Профиль», кадры модалки 440 r20 и успеха 312 r20). Позиция — одна строка
// заказа: карточка #F8F8F8 r20 с фото 100×123 r16 и счётчиком; причины —
// DS-радио 20, подписи 16/21 w500; заголовки 16/20 w600; комментарий — DS
// Textarea; фото/видео до 10 (не менее двух), подсказка 14/18; «Отправить» —
// DS Button lg, серая, пока форма не готова (кадр 3945:42520). Успех — 416 r20
// #F8F8F8, текст 14/18 по центру (3945:42891).
import { Loader2, Play, X } from "lucide-react";
import { useRef, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { api, RETURN_REASON_LABEL, reasonNeedsComment, type ReturnAttachment, type ReturnableLine, type ReturnReasonCode } from "@/api";
import { Download16Icon } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { useFormatPrice } from "@/lib/use-format-price";
import { cn } from "@/lib/utils";
import { CabinetModal, ProductTile, QtyStepper, Thumb } from "./return-parts";
const MAX_MEDIA = 10;
const MIN_MEDIA = 2;
const REASONS = Object.keys(RETURN_REASON_LABEL) as ReturnReasonCode[];
export type ReturnTarget = {
  orderId: string;
  line: ReturnableLine;
  brand?: string | null;
};
export function ReturnRequestModal(t0) {
  const $ = _c(7);
  const {
    target,
    onOpenChange,
    onCreated
  } = t0;
  const t1 = Boolean(target);
  let t2;
  if ($[0] !== onCreated || $[1] !== target) {
    t2 = target ? <ReturnForm key={`${target.orderId}-${target.line.orderLineId}`} target={target} onCreated={onCreated} /> : null;
    $[0] = onCreated;
    $[1] = target;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== onOpenChange || $[4] !== t1 || $[5] !== t2) {
    t3 = <CabinetModal open={t1} onOpenChange={onOpenChange} title={"\u0417\u0430\u044F\u0432\u043A\u0430 \u043D\u0430 \u0432\u043E\u0437\u0432\u0440\u0430\u0442"}>{t2}</CabinetModal>;
    $[3] = onOpenChange;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
function ReturnForm({
  target,
  onCreated
}: {
  target: ReturnTarget;
  onCreated: (s: {
    photo?: string | null;
    quantity: number;
  }) => void;
}) {
  const fmt = useFormatPrice();
  const {
    line
  } = target;
  const [qty, setQty] = useState(1);
  const [reason, setReason] = useState<ReturnReasonCode | null>(null);
  const [comment, setComment] = useState("");
  const [media, setMedia] = useState<ReturnAttachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  // «Другое» (правка №14): без комментария заявку не отправить — шлюз тоже
  // её не примет. Подсказку показываем, как только выбрали «Другое».
  const commentRequired = reasonNeedsComment(reason);
  const commentMissing = commentRequired && !comment.trim();
  const ready = Boolean(reason) && !commentMissing && media.length >= MIN_MEDIA && !uploading;
  const upload = async (files: FileList | null) => {
    if (!files?.length) return;
    const room = MAX_MEDIA - media.length;
    const picked = Array.from(files).slice(0, room);
    setUploading(true);
    try {
      for (const file of picked) {
        const att = await api.cabinet.uploadReturnAttachment(file);
        setMedia(prev => [...prev, att]);
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Не удалось загрузить файл");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };
  const submit = async () => {
    if (!reason || !ready) return;
    setSending(true);
    try {
      await api.cabinet.createReturn({
        orderId: target.orderId,
        lines: [{
          orderLineId: line.orderLineId,
          quantity: qty,
          reason,
          comment: comment.trim() || undefined,
          attachmentIds: media.map(m => m.id)
        }]
      });
      onCreated({
        photo: line.photo,
        quantity: qty
      });
    } catch (e_0) {
      toast.error(e_0 instanceof Error ? e_0.message : "Не удалось отправить заявку");
    } finally {
      setSending(false);
    }
  };
  return <div className="flex flex-col">
			<div className="flex gap-3 rounded-[20px] bg-[#F8F8F8] p-2">
				<ProductTile src={line.photo} w={100} h={123} />
				<div className="flex min-w-0 flex-1 flex-col">
					<span className="text-[14px] leading-[14px] font-bold tracking-[-0.42px] text-[#333]">{fmt(line.unitPrice)}</span>
					{target.brand ? <span className="mt-2 truncate text-[14px] leading-[15px] font-semibold tracking-[-0.28px] text-ds-ink">{target.brand}</span> : null}
					<span className="mt-1 line-clamp-2 text-[12px] leading-4 font-medium tracking-[-0.24px] text-[#797979]">
						{[line.name, line.variant].filter(Boolean).join(", ")}
					</span>
					<QtyStepper className="mt-auto self-start" value={qty} max={line.availableQuantity} onChange={setQty} />
				</div>
			</div>

			<p className="mt-5 text-[16px] leading-5 font-semibold tracking-[-0.31px] text-ds-primary">Выберите причину</p>
			<RadioGroup value={reason ?? ""} onValueChange={v => setReason(v as ReturnReasonCode)} aria-label="Причина возврата" className="mt-3 gap-3">
				{REASONS.map(code => {
        // Индивидуальный заказ надлежащего качества не возвращается
        // (fd-request-14): «не подошёл» для него недоступен.
        const blocked = Boolean(line.customized) && code === "not_suitable";
        return <label key={code} className="flex cursor-pointer items-center gap-2.5 text-[16px] leading-[21px] font-medium text-ds-primary has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-40">
							<RadioGroupItem value={code} disabled={blocked} />
							{RETURN_REASON_LABEL[code]}
						</label>;
      })}
			</RadioGroup>
			{line.customized ? <p className="mt-2 text-[12px] leading-4 text-[#1F1F1F]/48">
					Это индивидуальный заказ: вернуть его можно при браке, повреждениях или несоответствии заказу.
				</p> : null}

			<label className="mt-4 text-[16px] leading-5 font-semibold tracking-[-0.31px] text-ds-primary" htmlFor="return-comment">
				Комментарий{commentRequired ? <span className="text-ds-error"> *</span> : null}
			</label>
			<Textarea id="return-comment" value={comment} onChange={e_1 => setComment(e_1.target.value)} maxLength={500} rows={2} required={commentRequired} aria-invalid={commentMissing || undefined} aria-describedby={commentRequired ? "return-comment-hint" : undefined} placeholder={commentRequired ? "Опишите, что не так с товаром" : "Любой текст, который поможет принять решение о возврате"} className="mt-2 min-h-[66px] resize-none" />
			{commentRequired ? <p id="return-comment-hint" className={cn("mt-1 text-[12px] leading-4", commentMissing ? "text-ds-error" : "text-[#1F1F1F]/48")}>
					Для причины «Другое» комментарий обязателен.
				</p> : null}

			<p className="mt-4 text-[16px] leading-5 font-semibold tracking-[-0.31px] text-ds-primary">
				Добавьте фото и видео ({media.length}/{MAX_MEDIA})
			</p>
			<p className="mt-1 max-w-[360px] text-[14px] leading-[18px] text-ds-primary">
				Загрузите не менее двух качественных фотографий, на которых будут отчетливо видны товар и основание для возврата.
			</p>
			{media.length > 0 ? <div className="mt-2 flex flex-wrap gap-1">
					{media.map(m_0 => <span key={m_0.id} className="relative">
							{m_0.kind === "video" ? <span className="relative block h-[53px] w-10 overflow-hidden rounded-[4px] bg-[#1F1F1F]/10">
									<Play className="absolute top-1/2 left-1/2 size-4 -translate-x-1/2 -translate-y-1/2 fill-white text-white" />
								</span> : <Thumb src={m_0.thumbnailUrl || m_0.url} w={40} h={53} />}
							<button type="button" aria-label="Убрать файл" onClick={() => setMedia(prev_0 => prev_0.filter(x => x.id !== m_0.id))} className="absolute -top-1 -right-1 grid size-4 place-items-center rounded-full bg-[#2E2E2E] text-white">
								<X className="size-3" />
							</button>
						</span>)}
				</div> : null}
			<input ref={fileRef} type="file" accept="image/*,video/*" multiple hidden onChange={e_2 => void upload(e_2.target.files)} />
			<Button variant="gray" size="md" disabled={uploading || media.length >= MAX_MEDIA} onClick={() => fileRef.current?.click()} className="mt-3 self-start">
				{uploading ? <Loader2 className="size-4 animate-spin" /> : <Download16Icon size={16} />}
				Загрузить фото
			</Button>

			<Button block disabled={!ready || sending} onClick={() => void submit()} className="mt-5">
				{sending ? "Отправляем…" : "Отправить"}
			</Button>
		</div>;
}

/** «Ваша заявка … принята!» — 312 r20 на #F8F8F8, миниатюра 77×102, кнопка к заявкам. */
export function ReturnAcceptedModal(t0) {
  const $ = _c(15);
  const {
    open,
    onOpenChange,
    title: t1,
    photo,
    quantity,
    days
  } = t0;
  const title = t1 === undefined ? "\u0412\u0430\u0448\u0430 \u0437\u0430\u044F\u0432\u043A\u0430 \u043D\u0430 \u0432\u043E\u0437\u0432\u0440\u0430\u0442 \u043F\u0440\u0438\u043D\u044F\u0442\u0430!" : t1;
  const navigate = useNavigate();
  const t2 = days ? ` в течение ${days}` : "";
  let t3;
  if ($[0] !== t2) {
    t3 = <p className="-mt-2 text-center text-[14px] leading-[18px] text-ds-primary">Мы получили вашу заявку на возврат. Продавец приступит к её рассмотрению{t2}. Пожалуйста, дождитесь нашего уведомления о результатах рассмотрения.</p>;
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const t4 = `${quantity} шт.`;
  let t5;
  if ($[2] !== photo || $[3] !== t4) {
    t5 = <Thumb src={photo} label={t4} w={77} h={102} className="mx-auto mt-5" />;
    $[2] = photo;
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== navigate || $[6] !== onOpenChange) {
    t6 = <Button variant="gray" size="md" onClick={() => {
      onOpenChange(false);
      navigate("/profile/returns");
    }} className="mx-auto mt-5">Перейти ко всем заявкам</Button>;
    $[5] = navigate;
    $[6] = onOpenChange;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== onOpenChange || $[9] !== open || $[10] !== t3 || $[11] !== t5 || $[12] !== t6 || $[13] !== title) {
    t7 = <CabinetModal open={open} onOpenChange={onOpenChange} title={title} width={416} className="bg-[#F8F8F8] px-4 py-6" titleClassName="flex-1 pt-0 pl-8 text-center text-[16px] leading-5 font-semibold tracking-[-0.31px] text-ds-ink">{t3}{t5}{t6}</CabinetModal>;
    $[8] = onOpenChange;
    $[9] = open;
    $[10] = t3;
    $[11] = t5;
    $[12] = t6;
    $[13] = title;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  return t7;
}